import type {
  IViewChoicesPatch,
  IViewChoicesPatchResult,
  IViewChoicesPreferences,
  IViewChoicesScope,
  IViewChoicesState,
} from '@featit/shared';
import type { IViewChoicesSendOptions } from '@/features/views/viewChoicesSync';
import { API_BASE_URL, ApiService } from './ApiService';
import { requestAccessToken } from './accessToken';

function scopeParams(scope: IViewChoicesScope): Record<string, string> {
  return {
    scopeKind: scope.scopeKind,
    viewId: scope.viewId,
    ...(scope.scopeId ? { scopeId: scope.scopeId } : {}),
  };
}

/**
 * F-0527 (ADR-165): a person's view choices on the server, and the setting that turns them on. The
 * caller's own rows only; nothing here names a user.
 */
class ViewChoicesApiService extends ApiService {
  read(scope: IViewChoicesScope): Promise<IViewChoicesState> {
    return this.get('/user-view-states', { params: scopeParams(scope) });
  }

  /**
   * The merged state and which fields applied; a field kept at the server's newer value is not an error.
   *
   * `outliveThePage` (I-1287): the page is going away, and a request of its own would go with it.
   * This one is a plain `fetch` with `keepalive`, sent before this returns, with the session read up
   * front. It does not renew a lapsed session: a write refused for that stays pending on the device
   * and goes with the next ordinary send.
   */
  patchFields(
    patch: IViewChoicesPatch,
    options: IViewChoicesSendOptions = {},
  ): Promise<IViewChoicesPatchResult> {
    if (!options.outliveThePage) return this.patch('/user-view-states', patch);
    let accessToken: string | null = null;
    try {
      accessToken = requestAccessToken();
    } catch {
      // No readable session: the server refuses it, and the write stays pending.
    }
    return fetch(`${API_BASE_URL}/user-view-states`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(patch),
      keepalive: true,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Your view choices were not saved while the page was leaving (${response.status}). They are kept on this device and sent the next time the view opens.`,
        );
      }
      return response.json() as Promise<IViewChoicesPatchResult>;
    });
  }

  /** Reset view: every live field cleared, on every device. */
  reset(scope: IViewChoicesScope): Promise<IViewChoicesState> {
    return this.delete('/user-view-states', { params: scopeParams(scope) });
  }

  preferences(): Promise<IViewChoicesPreferences> {
    return this.get('/user-preferences/view-choices');
  }

  updatePreferences(keepAcrossDevices: boolean): Promise<IViewChoicesPreferences> {
    return this.patch('/user-preferences/view-choices', { keepAcrossDevices });
  }
}

export const viewChoicesApi = new ViewChoicesApiService();
